import { useEffect, useRef, useState } from "react";
import { gpsDriveGet } from "@/lib/api/gpsdrive-client";

// Cache global em memória para evitar requisições repetidas.
const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string | null>>();

// Persistência entre sessões (localStorage) — mesmas ruas/paradas se
// repetem entre rotas e dias, então vale reaproveitar o cache já pago.
// v2 (2026-10-01): backend passou a pedir o nível de prédio (com número da
// casa) — o cache v1 só tinha a rua, por isso a troca de chave.
const PERSIST_KEY = "gpsdrive.reverseGeocode.cache.v2";
const PERSIST_MAX_ENTRIES = 3000;

function loadPersistedCache() {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw) as Record<string, string>;
    for (const [k, v] of Object.entries(obj)) cache.set(k, v);
  } catch {
    // localStorage indisponível (aba anônima/quota) — cache fica só em memória.
  }
}
loadPersistedCache();

// Relato 2026-10-05 ("quando fico muito tempo com a tela aberta começa a
// bugar"): com a frota inteira na tela, o navegador fazia ~1 consulta/s e o
// `cache` em memória crescia sem limite (10 mil+ entradas em 3h). A cada
// endereço novo, persistCache copiava o Map INTEIRO e gravava ~300 KB no
// localStorage de forma síncrona — custo que crescia com o tempo de tela
// aberta. Agora o Map fica limitado (descarta os mais antigos — Map mantém
// ordem de inserção) e a gravação é agrupada a cada PERSIST_DEBOUNCE_MS.
const MEMORY_MAX_ENTRIES = PERSIST_MAX_ENTRIES * 2;
const PERSIST_DEBOUNCE_MS = 10_000;
let persistTimer: ReturnType<typeof setTimeout> | undefined;

function cacheSet(k: string, v: string) {
  cache.delete(k);
  cache.set(k, v);
  while (cache.size > MEMORY_MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

function persistCache() {
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = undefined;
    try {
      // Mantém só as entradas mais recentes pra não crescer sem limite.
      const entries = [...cache.entries()].slice(-PERSIST_MAX_ENTRIES);
      localStorage.setItem(PERSIST_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch {
      // quota indisponível/excedida — cache persistente é só otimização.
    }
  }, PERSIST_DEBOUNCE_MS);
}

// No máximo GEOCODE_MAX_INFLIGHT consultas abertas ao mesmo tempo (as de
// `priority` furam a fila): sem isso, dezenas de cartões pedindo endereço
// juntos ocupavam todas as conexões do navegador com api.gpsdrive.com.br e
// a foto do veículo, o Editar dispositivo e os Alertas ficavam esperando
// atrás delas (mesmo relato 2026-10-05).
const GEOCODE_MAX_INFLIGHT = 4;
let geocodeRunning = 0;
const geocodeQueue: Array<() => void> = [];

async function withGeocodeSlot<T>(priority: boolean, fn: () => Promise<T>): Promise<T> {
  if (geocodeRunning >= GEOCODE_MAX_INFLIGHT) {
    await new Promise<void>((resolve) => {
      if (priority) geocodeQueue.unshift(resolve);
      else geocodeQueue.push(resolve);
    });
  }
  geocodeRunning++;
  try {
    return await fn();
  } finally {
    geocodeRunning--;
    geocodeQueue.shift()?.();
  }
}

function keyOf(lat: number, lng: number) {
  // ~11m de precisão; agrupa consultas próximas
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

// Geocodificação reversa passa por um proxy no nosso backend (não mais
// fetch direto pro Nominatim do navegador) — a CDN do Nominatim às vezes
// serve resposta cacheada SEM Access-Control-Allow-Origin pra coordenadas
// populares/repetidas, o que o browser bloqueia silenciosamente. Um veículo
// parado/offline sempre pede a MESMA coordenada, então esse bug fazia o
// endereço nunca aparecer pra ele (bug relatado 2026-08-31, veículo J17).
// Servidor-a-servidor não tem CORS, e o próprio backend agora cuida do
// limite de ~1 req/s do Nominatim (ver geocoding.service.ts) — não precisa
// mais de fila/intervalo aqui.
// `deviceId`: o servidor grava como último endereço daquele veículo
// (2026-10-03) — qualquer navegador/celular passa a ver na hora.
export async function reverseGeocode(
  lat: number,
  lng: number,
  priority = false,
  deviceId?: string,
): Promise<string | null> {
  const k = keyOf(lat, lng);
  if (cache.has(k)) return cache.get(k)!;
  const existing = inflight.get(k);
  if (existing) return existing;

  const p = (async () => {
    try {
      const { address } = await withGeocodeSlot(priority, () =>
        gpsDriveGet<{ address: string | null }>(
          `/api/v1/geocode/reverse?lat=${lat}&lng=${lng}${priority ? "&priority=1" : ""}${
            deviceId && /^\d+$/.test(deviceId) ? `&deviceId=${deviceId}` : ""
          }`,
        ),
      );
      if (address) {
        cacheSet(k, address);
        persistCache();
      }
      return address;
    } catch (err) {
      console.warn(`[reverseGeocode] falha ao buscar endereço para ${lat},${lng}`, err);
      return null;
    }
  })();
  inflight.set(k, p);
  try {
    return await p;
  } finally {
    inflight.delete(k);
  }
}

// Último endereço conhecido POR VEÍCULO (localStorage) — pedido 2026-10-01:
// "às vezes fica mostrando Localizando endereço, isso nunca pode
// acontecer, sempre tem que ficar fixo o endereço da última posição".
// Enquanto o endereço da posição nova não chega (ou se a consulta falhar),
// continua mostrando o último endereço daquele veículo em vez do texto de
// carregamento.
const LAST_KEY = "gpsdrive.reverseGeocode.lastByVehicle.v1";
let lastByVehicle: Record<string, string> = {};
try {
  lastByVehicle = JSON.parse(localStorage.getItem(LAST_KEY) ?? "{}") as Record<string, string>;
} catch {
  lastByVehicle = {};
}
// Gravação agrupada (mesmo motivo do persistCache acima): o objeto tem a
// frota inteira e era reescrito no localStorage a cada endereço novo.
let lastPersistTimer: ReturnType<typeof setTimeout> | undefined;
function rememberLast(stableKey: string, addr: string) {
  if (lastByVehicle[stableKey] === addr) return;
  lastByVehicle[stableKey] = addr;
  if (lastPersistTimer) return;
  lastPersistTimer = setTimeout(() => {
    lastPersistTimer = undefined;
    try {
      localStorage.setItem(LAST_KEY, JSON.stringify(lastByVehicle));
    } catch {
      // quota/anônimo — fica só em memória.
    }
  }, PERSIST_DEBOUNCE_MS);
}

// Último endereço gravado NO SERVIDOR por veículo (2026-10-03, pedido:
// "Localizando endereço… fica dando isso, deixar gravado sempre a última
// posição"). O localStorage acima só vale pro navegador que já viu o
// veículo; o servidor grava a frota inteira em segundo plano. Pedidos de
// vários cartões no mesmo instante viram UMA chamada (?deviceIds=1,2,3).
const serverLast = new Map<string, string | null>();
let pendingIds = new Set<string>();
let pendingWaiters: Array<() => void> = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;

function flushServerLast() {
  flushTimer = undefined;
  const ids = [...pendingIds];
  const waiters = pendingWaiters;
  pendingIds = new Set();
  pendingWaiters = [];
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200));
  Promise.all(
    chunks.map((chunk) =>
      gpsDriveGet<Record<string, { address: string }>>(`/api/v1/geocode/last?deviceIds=${chunk.join(",")}`)
        .then((res) => {
          for (const id of chunk) serverLast.set(id, res[id]?.address ?? null);
        })
        .catch(() => {
          /* backend fora — fica com o que tiver local */
        }),
    ),
  ).finally(() => waiters.forEach((w) => w()));
}

function fetchServerLast(deviceId: string): Promise<string | null> {
  if (serverLast.has(deviceId)) return Promise.resolve(serverLast.get(deviceId) ?? null);
  return new Promise((resolve) => {
    pendingIds.add(deviceId);
    pendingWaiters.push(() => resolve(serverLast.get(deviceId) ?? null));
    if (!flushTimer) flushTimer = setTimeout(flushServerLast, 30);
  });
}

// Falha (Nominatim lento/fora) nunca deixa o texto travado: tenta de novo
// com espera crescente enquanto a tela estiver aberta.
const RETRY_DELAYS_MS = [3000, 8000, 15000, 30000, 60000];

export function useReverseGeocode(
  lat: number | undefined,
  lng: number | undefined,
  opts: { stableKey?: string; priority?: boolean } = {},
): string | null {
  const { stableKey, priority = false } = opts;
  const k = lat != null && lng != null ? keyOf(lat, lng) : "";
  const deviceId = stableKey && /^\d+$/.test(stableKey) ? stableKey : undefined;
  const initial = () =>
    (k ? cache.get(k) : undefined) ??
    (stableKey ? lastByVehicle[stableKey] : undefined) ??
    (deviceId ? serverLast.get(deviceId) : undefined) ??
    null;
  const [addr, setAddr] = useState<string | null>(initial);

  // Trocou de veículo: nunca mostra o endereço do anterior.
  const prevStableKey = useRef(stableKey);
  if (prevStableKey.current !== stableKey) {
    prevStableKey.current = stableKey;
    const next = initial();
    if (next !== addr) setAddr(next);
  }

  // Nada local: busca o último endereço gravado no servidor enquanto a
  // consulta da posição atual corre (que tem prioridade quando chegar).
  const gotFresh = useRef(false);
  useEffect(() => {
    gotFresh.current = false;
    if (!deviceId || addr) return;
    let cancelled = false;
    fetchServerLast(deviceId).then((v) => {
      if (!cancelled && v && !gotFresh.current) setAddr((cur) => cur ?? v);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  useEffect(() => {
    if (lat == null || lng == null) return;
    const key = keyOf(lat, lng);
    const cached = cache.get(key);
    if (cached) {
      setAddr(cached);
      if (stableKey) rememberLast(stableKey, cached);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = (n: number) => {
      reverseGeocode(lat, lng, priority, deviceId).then((v) => {
        if (cancelled) return;
        if (v) {
          gotFresh.current = true;
          setAddr(v);
          if (stableKey) rememberLast(stableKey, v);
          return;
        }
        timer = setTimeout(() => attempt(n + 1), RETRY_DELAYS_MS[Math.min(n, RETRY_DELAYS_MS.length - 1)]);
      });
    };
    attempt(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Só refaz se a chave arredondada mudar (evita spam durante animação).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k, stableKey]);

  return addr;
}

// ============================================================================
// Geocoding em lote (Histórico de rota) — nunca 1 chamada por posição.
//
// Estratégia: agrupa posições próximas (~130m) num único cluster e resolve
// o endereço só uma vez por cluster (no centróide), reaproveitando pro
// resto do grupo. Some com o cache/fila acima, então clusters já vistos em
// sessões anteriores saem do localStorage sem nenhuma chamada de rede.
// ============================================================================

const CLUSTER_GRID_DEG = 0.0013; // ~130m em latitude — pontos nesse raio reaproveitam o mesmo endereço

function clusterKeyOf(lat: number, lng: number): string {
  return `${Math.round(lat / CLUSTER_GRID_DEG)},${Math.round(lng / CLUSTER_GRID_DEG)}`;
}

export interface GeocodableFix {
  id: string | number;
  lat: number;
  lng: number;
  address?: string | null;
}

// Resolve endereço pra um conjunto de posições, priorizando sempre o
// `address` que já vier pronto (ex.: do Traccar, se algum dia tiver
// geocoder configurado) — só geocodifica o que realmente falta, e por
// cluster, não por posição. `datasetKey` identifica a rota carregada
// (veículo+período) pra não reprocessar a cada render.
export function useBatchReverseGeocode(fixes: GeocodableFix[], datasetKey: string): Map<string | number, string> {
  const [resolved, setResolved] = useState<Map<string | number, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    setResolved(new Map());

    const missing = fixes.filter((f) => !f.address);
    if (missing.length === 0) return;

    const clusters = new Map<string, { sumLat: number; sumLng: number; count: number; ids: (string | number)[] }>();
    for (const f of missing) {
      const key = clusterKeyOf(f.lat, f.lng);
      const c = clusters.get(key);
      if (c) {
        c.sumLat += f.lat;
        c.sumLng += f.lng;
        c.count += 1;
        c.ids.push(f.id);
      } else {
        clusters.set(key, { sumLat: f.lat, sumLng: f.lng, count: 1, ids: [f.id] });
      }
    }

    const next = new Map<string | number, string>();
    (async () => {
      for (const cluster of clusters.values()) {
        if (cancelled) return;
        const addr = await reverseGeocode(cluster.sumLat / cluster.count, cluster.sumLng / cluster.count);
        if (cancelled) return;
        if (addr) {
          for (const id of cluster.ids) next.set(id, addr);
          setResolved(new Map(next));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // `fixes` entra na lista de dependências de propósito: no primeiro
    // render após trocar de veículo/período, a query de posições ainda não
    // respondeu, então `fixes` chega vazio e o efeito não teria mais motivo
    // pra rodar de novo quando os dados reais chegassem, já que
    // `datasetKey` (veículo+período) não muda nesse meio tempo — o endereço
    // nunca era resolvido no fluxo normal por causa disso. `fixes` só troca
    // de referência quando `filteredPositions` muda de verdade (é
    // memoizado no componente que chama este hook), então não gera loop.
  }, [datasetKey, fixes]);

  return resolved;
}
